import { Form, Formik, FormikProps } from 'formik';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { useAppDispatch, useAppSelector } from '@/reducers';
import { getS3FilePreviewKey, useGetS3FilePreviewMutation } from '@/services/endpoints/files';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { updateFileData } from '@/features/fileUploading/fileUploadingSlice';
import { StepPageActions } from '@/global/components';
import { DataSource, DataSourceAmazonS3Config, DataSourceType } from '@/models/dataSource';
import { Input } from '@/components/inputs';

export type AmazonS3FormModel = Partial<DataSource<DataSourceAmazonS3Config>>;

type NameAndBucketStepProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
};

const CSV_ROWS_NUMBER = 5;

export const NameAndBucketStep: FC<NameAndBucketStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dataSource = useAppSelector((state) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceAmazonS3Config>> | undefined;

  const formRef = useRef<FormikProps<AmazonS3FormModel>>(null);

  const [getS3FilePreview, { isLoading }] = useGetS3FilePreviewMutation({
    fixedCacheKey: getS3FilePreviewKey,
  });

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
    configuration: Yup.object({
      accessKey: Yup.string().required(t('data_source_edit:validations.required')),
      secretKey: Yup.string().required(t('data_source_edit:validations.required')),
      bucketArn: Yup.string().required(t('data_source_edit:validations.required')),
      path: Yup.string(),
      region: Yup.string().required(t('data_source_edit:validations.required')),
      roleArn: Yup.string(),
    }),
  });

  const handleSubmit = async (form: AmazonS3FormModel) => {
    const response = await getS3FilePreview({ ...form.configuration, limit: CSV_ROWS_NUMBER });
    if ('error' in response) {
      throw new Error(JSON.stringify(response.error));
    }

    if ('data' in response) {
      const matrix = response.data.map((row) => row.split(','));

      dispatch(updateFileData({ preview: matrix?.slice(0, CSV_ROWS_NUMBER), header: matrix[0], isDefaultHeader: true }));
    }
  };

  const handleNextStep = async () => {
    await formRef.current?.submitForm();

    if (formRef.current?.isValid && formRef.current?.values) {
      dispatch(stashDataSource({ ...formRef.current.values, type: dataSourceType }));

      onNextStep();
    }
  };

  return (
    <>
      <Formik
        innerRef={formRef}
        initialValues={
          dataSource
            ? {
                ...dataSource,
                name: '',
              }
            : {
                name: '',
                configuration: {
                  accessKey: '',
                  secretKey: '',
                  bucketArn: '',
                  path: '',
                  region: '',
                  roleArn: '',
                },
              }
        }
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={validationSchema}
      >
        {({ values, errors, handleChange, setFieldError }) => {
          return (
            <Form>
              <div className='rounded-lg border border-solid border-gray-200 p-6'>
                <Input
                  name='name'
                  id='name'
                  type='text'
                  label={t('data_source_edit:code.source_details.source_name')}
                  value={values?.name}
                  placeholder={t('data_source_edit:amazon_s3.source_name_placeholder')}
                  onChange={(e) => {
                    setFieldError('name', undefined);
                    handleChange('name')(e.target.value);
                  }}
                  error={errors?.name}
                />
              </div>
              <label className='capitalize-first mt-4 block py-2 ps-6 text-left text-sm font-medium text-gray-700'>
                {t('data_source_edit:amazon_s3.credentials')}
              </label>
              <div className='rounded-lg border border-solid border-gray-200 p-6'>
                <Input
                  name='configuration.accessKey'
                  id='configuration.accessKey'
                  type='text'
                  label={t('data_source_edit:amazon_s3.keyId')}
                  labelClassName='capitalize'
                  value={values?.configuration.accessKey}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.accessKey', undefined);
                    handleChange('configuration.accessKey')(e.target.value);
                  }}
                  error={errors?.configuration?.accessKey}
                />
                <Input
                  name='configuration.secretKey'
                  id='configuration.secretKey'
                  type='text'
                  label={t('data_source_edit:amazon_s3.secretAccessKey')}
                  labelClassName='capitalize'
                  value={values?.configuration.secretKey}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.secretKey', undefined);
                    handleChange('configuration.secretKey')(e.target.value);
                  }}
                  error={errors?.configuration?.secretKey}
                />
                <Input
                  name='configuration.bucketArn'
                  id='configuration.bucketArn'
                  type='text'
                  label={t('data_source_edit:amazon_s3.bucket')}
                  labelClassName='capitalize'
                  value={values?.configuration.bucketArn}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.bucketArn', undefined);
                    handleChange('configuration.bucketArn')(e.target.value);
                  }}
                  error={errors?.configuration?.bucketArn}
                />
                <Input
                  name='configuration.path'
                  id='configuration.path'
                  type='text'
                  label={t('data_source_edit:amazon_s3.path')}
                  labelClassName='capitalize'
                  value={values?.configuration.path}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.path', undefined);
                    handleChange('configuration.path')(e.target.value);
                  }}
                  error={errors?.configuration?.path}
                />
                <Input
                  name='configuration.region'
                  id='configuration.region'
                  type='text'
                  label={t('data_source_edit:amazon_s3.region')}
                  labelClassName='capitalize'
                  value={values?.configuration.region}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.region', undefined);
                    handleChange('configuration.region')(e.target.value);
                  }}
                  error={errors?.configuration?.region}
                />
                <Input
                  name='configuration.roleArn'
                  id='configuration.roleArn'
                  type='text'
                  label={t('data_source_edit:amazon_s3.role')}
                  labelClassName='capitalize'
                  value={values?.configuration.roleArn}
                  placeholder={t('data_source_edit:amazon_s3.insertTextHere')}
                  onChange={(e) => {
                    setFieldError('configuration.roleArn', undefined);
                    handleChange('configuration.roleArn')(e.target.value);
                  }}
                  error={errors?.configuration?.roleArn}
                />
              </div>
            </Form>
          );
        }}
      </Formik>

      <StepPageActions
        primaryButton={{
          text: 'COMMON.confirm',
          onClick: handleNextStep,
          isLoading: isLoading,
        }}
        secondaryButton={{
          text: 'COMMON.cancel',
          onClick: onCancel,
        }}
      />
    </>
  );
};
